import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X, ArrowUpCircle } from 'lucide-react';
import Link from '@/components/LocaleLink';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface UsageAlert {
  key: string;
  level: 'warning' | 'critical' | 'capped';
  current: number;
  limit: number;
  percent: number;
  addon_price?: number;
  unit?: string;
}

const LIMIT_NAMES: Record<string, string> = {
  storage_gb: 'storage',
  products: 'products',
  sites: 'sites',
  shops: 'shops',
  courses: 'courses',
  podcasts: 'podcasts',
  pages: 'pages',
  members: 'community members',
  ai_credits_monthly: 'AI credits',
  email_sends_monthly: 'monthly emails',
  stream_minutes: 'streaming minutes',
  custom_domains: 'custom domains',
  team_members: 'team members',
};

function formatValue(key: string, value: number): string {
  if (key === 'storage_gb') return `${value} GB`;
  if (key === 'email_sends_monthly') {
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return `${value}`;
  }
  if (key === 'stream_minutes') return `${value} min`;
  return `${value}`;
}

export default function UsageAlertBanner() {
  const { t } = useTranslation();
  const [alerts, setAlerts] = useState<UsageAlert[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(() => {
    try {
      const stored = sessionStorage.getItem('soltik_dismissed_alerts');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  const fetchAlerts = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    // Superadmins bypass all limits — don't show alerts
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.role === 'superadmin') return;
    } catch {}
    try {
      const res = await fetch(`${API_URL}/usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
      }
    } catch (e) {
      console.error('Failed to fetch usage alerts:', e);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleDismiss = (key: string) => {
    const next = new Set(dismissed);
    next.add(key);
    setDismissed(next);
    try {
      sessionStorage.setItem('soltik_dismissed_alerts', JSON.stringify([...next]));
    } catch {}
  };

  const visibleAlerts = alerts.filter((a) => {
    // Critical and capped are never dismissible
    if (a.level === 'critical' || a.level === 'capped') return true;
    // Warning can be dismissed
    return !dismissed.has(a.key);
  });

  if (visibleAlerts.length === 0) return null;

  return (
    <div className="space-y-0">
      {visibleAlerts.map((alert) => {
        const name = LIMIT_NAMES[alert.key] || alert.key;
        const currentFormatted = formatValue(alert.key, alert.current);
        const limitFormatted = formatValue(alert.key, alert.limit);
        const isCapped = alert.level === 'capped';
        const isCritical = alert.level === 'critical';
        const isWarning = alert.level === 'warning';

        const bgColor = isWarning
          ? 'bg-yellow-500/10 border-yellow-500/30'
          : 'bg-red-500/10 border-red-500/30';
        const textColor = isWarning ? 'text-yellow-300' : 'text-red-300';
        const iconColor = isWarning ? 'text-yellow-400' : 'text-red-400';

        return (
          <div
            key={alert.key}
            className={`${bgColor} border-b px-4 py-2.5 flex items-center justify-between gap-3`}
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <AlertTriangle size={15} className={`${iconColor} shrink-0`} />
              <span className={`text-sm ${textColor}`}>
                {isCapped
                  ? t('usage.alertCapped', {
                      defaultValue: `You've reached your ${name} limit (${limitFormatted}). Upgrade to continue.`,
                      resource: name,
                      limit: limitFormatted,
                    })
                  : t('usage.alertUsage', {
                      defaultValue: `You've used ${currentFormatted} of ${limitFormatted} ${name}.`,
                      current: currentFormatted,
                      limit: limitFormatted,
                      resource: name,
                    })}
              </span>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {alert.addon_price != null && (
                <Link
                  to="/dashboard/addons"
                  className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-iris-600 hover:bg-iris-500 text-[#0C0C12] rounded-md transition-colors"
                >
                  {isCapped ? (
                    <>
                      <ArrowUpCircle size={12} />
                      {t('usage.upgrade', 'Upgrade')}
                    </>
                  ) : (
                    t('usage.buyMore', {
                      defaultValue: `Buy more — $${alert.addon_price}/mo`,
                      price: alert.addon_price,
                    })
                  )}
                </Link>
              )}

              {!isCapped && !isCritical && (
                <Link
                  to="/dashboard/addons"
                  className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-iris-600 hover:bg-iris-500 text-[#0C0C12] rounded-md transition-colors"
                >
                  {t('usage.buyAddons', 'Buy Add-ons')}
                </Link>
              )}

              {isWarning && (
                <button
                  onClick={() => handleDismiss(alert.key)}
                  className="p-1 text-[#a0a0b0] hover:text-white transition-colors rounded"
                  aria-label={t('common.dismiss', 'Dismiss')}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
